/**
 * Helpers for edit-form concurrent save conflict:
 * snapshot baseline → keep dirty fields → reload latest → merge dirty over DB.
 * Three-way merge (baseline / draft / latest): only what this user changed is kept;
 * everything else comes from the DB. Subforms merge per row and cell.
 * MultiLookup: when dirty, keep the user's draft selection (deduped by id) — do not
 * re-merge ids already present from latest.
 */

/** Wait before re-reading after a 409 so the other in-flight save can commit and release its lock. */
export const CONFLICT_REFRESH_DELAY_MS = 2000;

const ID_VALUE_ELEMENTS = ['Lookup', 'MultiLookup'];

/** Prefer element_original (CustomAll / wrappers) then element. */
const resolveFieldElement = (field) => {
  if (!field) {
    return '';
  }
  return field.element_original || field.element || field.element_type || '';
};

const deepClone = (value) => {
  try {
    return JSON.parse(JSON.stringify(value));
  } catch (e) {
    return value;
  }
};

const FILE_IMAGE_ELEMENTS = {
  File: true,
  Image: true,
  FileUpload: true,
  ImageUpload: true,
  CustomAllFiles: true,
};

/** File/Image uploads are stored on field.files (not selectedValue). */
export const isFileOrImageField = (field) => {
  if (!field) {
    return false;
  }
  const el = field.element || field.element_original || field.element_type;
  return !!(el && FILE_IMAGE_ELEMENTS[el]);
};

/**
 * Stable compare for file lists (ignores non-JSON File blobs on entry.file).
 * Upload / remove / replace must change id, label, or size to count as dirty.
 */
export const normalizeFilesForCompare = (files) => {
  if (!Array.isArray(files) || files.length === 0) {
    return '';
  }
  try {
    return JSON.stringify(
      files.map((f) => ({
        id: f == null ? '' : f.id != null ? f.id : f.value != null ? f.value : '',
        label: f == null ? '' : f.label != null ? f.label : '',
        size: f == null ? '' : f.size != null ? f.size : '',
      }))
    );
  } catch (e) {
    return String(files.length);
  }
};

export const normalizeComparableValue = (value) => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value);
    } catch (e) {
      return String(value);
    }
  }
  return String(value);
};

export const valuesAreEqual = (a, b) => {
  return normalizeComparableValue(a) === normalizeComparableValue(b);
};

const CURRENCY_COMPANION_KEYS = ['currency_code', 'from_currency', 'exchange_rate'];

const isCurrencyField = (field) => {
  if (!field) {
    return false;
  }
  return field.element === 'Currency' || field.element_original === 'Currency';
};

const hasCurrencyCompanions = (source) => {
  if (!source) {
    return false;
  }
  return CURRENCY_COMPANION_KEYS.some((key) => source[key] !== undefined && source[key] !== null);
};

/**
 * Canonical field value for baseline + dirty checks.
 * File/Image → files list; Currency → amount + companions; else selectedValue.
 */
export const fieldComparableValue = (field) => {
  if (!field) {
    return undefined;
  }
  if (isFileOrImageField(field)) {
    if (Array.isArray(field.files)) {
      return normalizeFilesForCompare(field.files);
    }
    if (Array.isArray(field.selectedValue)) {
      return normalizeFilesForCompare(field.selectedValue);
    }
    return normalizeFilesForCompare([]);
  }
  if (!isCurrencyField(field) && !hasCurrencyCompanions(field)) {
    return field.selectedValue;
  }
  return {
    selectedValue: field.selectedValue,
    currency_code: field.currency_code ?? '',
    from_currency: field.from_currency ?? '',
    exchange_rate: field.exchange_rate ?? '',
  };
};

/** Alias kept for callers that used the File/Image helper name. */
export const getComparableFieldValue = fieldComparableValue;

/**
 * Subform column comparable: selected_value, plus currency companions and/or files when present.
 */
const columnComparableValue = (col) => {
  if (!col) {
    return undefined;
  }
  const hasCurrency = hasCurrencyCompanions(col);
  const hasFilesArray = Array.isArray(col.files);
  if (!hasCurrency && !hasFilesArray) {
    return col.selected_value;
  }
  const result = {
    v: col.selected_value,
  };
  if (hasCurrency) {
    result.currency_code = col.currency_code ?? '';
    result.from_currency = col.from_currency ?? '';
    result.exchange_rate = col.exchange_rate ?? '';
  }
  if (hasFilesArray) {
    result.f = normalizeFilesForCompare(col.files);
  }
  return result;
};

/** Legacy baselines stored only the amount. Compare that amount so untouched fields stay clean. */
const fieldValuesAreEqual = (current, baseline) => {
  const currentIsComposite = current && typeof current === 'object' && !Array.isArray(current)
    && Object.prototype.hasOwnProperty.call(current, 'selectedValue');
  const baselineIsComposite = baseline && typeof baseline === 'object' && !Array.isArray(baseline)
    && Object.prototype.hasOwnProperty.call(baseline, 'selectedValue');
  if (currentIsComposite && !baselineIsComposite) {
    return valuesAreEqual(current.selectedValue, baseline);
  }
  if (!currentIsComposite && baselineIsComposite) {
    return valuesAreEqual(current, baseline.selectedValue);
  }
  return valuesAreEqual(current, baseline);
};

/**
 * Subform column compare: plain selected_value vs composite {v, currency..., f}.
 * Missing companion keys on either side treat as empty so file/currency-only edits still dirty.
 */
const columnValuesAreEqual = (current, baseline) => {
  const toShape = (val) => {
    if (val && typeof val === 'object' && !Array.isArray(val)
      && Object.prototype.hasOwnProperty.call(val, 'v')) {
      return val;
    }
    return { v: val };
  };
  const c = toShape(current);
  const b = toShape(baseline);
  const keys = {};
  Object.keys(c).forEach((k) => { keys[k] = true; });
  Object.keys(b).forEach((k) => { keys[k] = true; });
  return Object.keys(keys).every((key) => valuesAreEqual(
    c[key] !== undefined && c[key] !== null ? c[key] : '',
    b[key] !== undefined && b[key] !== null ? b[key] : ''
  ));
};

/** Build fieldId → value map from layout fieldGroups (non-subform sections). */
export const extractFieldValueMap = (fieldGroups = []) => {
  const map = {};
  (fieldGroups || []).forEach((section) => {
    if (!section || section.type === 'subform') {
      return;
    }
    (section.data || []).forEach((field) => {
      if (field && field.id) {
        map[field.id] = fieldComparableValue(field);
      }
    });
  });
  return map;
};

/** Build sectionId → comparable subform rows map from Redux subformData. */
export const extractSubformValueMap = (subformData = []) => {
  const map = {};
  (subformData || []).forEach((sf) => {
    if (!sf || !sf.field_group_section_id) {
      return;
    }
    map[sf.field_group_section_id] = (sf.subform_rows || []).map((row) => ({
      id: row.id || '',
      columns: (row.subform_column_values || []).map((col) => ({
        id: col.table_descriptor_id,
        v: columnComparableValue(col),
      })),
    }));
  });
  return map;
};

/** Fallback when subform lives on fieldGroups.section.subform_rows (edit GET). */
export const extractSubformValueMapFromFieldGroups = (fieldGroups = []) => {
  const map = {};
  (fieldGroups || []).forEach((section) => {
    if (!section || section.type !== 'subform') {
      return;
    }
    map[section.id] = (section.subform_rows || []).map((row) => ({
      id: row.id || '',
      columns: (row.subform_column_values || []).map((col) => ({
        id: col.table_descriptor_id,
        v: columnComparableValue(col),
      })),
    }));
  });
  return map;
};

export const buildEditFormBaseline = (fieldGroups = [], subformData = []) => {
  const subformFromRedux = extractSubformValueMap(subformData);
  const subformFromGroups = extractSubformValueMapFromFieldGroups(fieldGroups);
  return {
    fieldValues: extractFieldValueMap(fieldGroups),
    subformValues: Object.keys(subformFromRedux).length > 0 ? subformFromRedux : subformFromGroups,
  };
};

/**
 * Lookup / MultiLookup values arrive as a bare id, "id1,id2", a JSON object or a JSON array,
 * and the field components rewrite the format after mount — compare by record ids only.
 */
export const uniqueIdList = (ids = []) => {
  const seen = {};
  const out = [];
  (ids || []).forEach((id) => {
    const key = String(id == null ? '' : id).trim();
    if (!key || seen[key]) {
      return;
    }
    seen[key] = true;
    out.push(key);
  });
  return out;
};

export const toIdList = (value) => {
  if (value === null || value === undefined || value === '') {
    return [];
  }
  // Currency composite baseline accidentally passed here: use selectedValue
  if (value && typeof value === 'object' && !Array.isArray(value)
    && Object.prototype.hasOwnProperty.call(value, 'selectedValue')) {
    return toIdList(value.selectedValue);
  }
  let parsed = value;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed.startsWith('[') || trimmed.startsWith('{')) {
      try {
        parsed = JSON.parse(trimmed);
      } catch (e) {
        parsed = trimmed;
      }
    }
  }
  if (typeof parsed === 'string') {
    return uniqueIdList(parsed.split(',').map((s) => s.trim()).filter(Boolean));
  }
  const list = Array.isArray(parsed) ? parsed : [parsed];
  return uniqueIdList(
    list
      .map((e) => (e && typeof e === 'object' ? multiLookupRecordId(e) : String(e || '')))
      .filter(Boolean)
  );
};

/**
 * Edit-form MultiLookup selectedValue is a LayoutJsonModel JSON array.
 * The related record id is metaTableId (what the tag component stores as rowId).
 * `id` on that payload is not always the selected record, so it must not win the compare.
 */
const multiLookupRecordId = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return '';
  }
  return String(
    entry.metaTableId || entry.meta_table_id || entry.value || entry.rowId || entry.id || ''
  ).trim();
};

const sameIdSet = (a, b) => a.length === b.length && a.every((id) => b.indexOf(id) !== -1);

const isFieldDirty = (fieldId, currentValue, baseline, element) => {
  const hasBaselineMap = baseline && baseline.fieldValues
    && Object.keys(baseline.fieldValues).length > 0;
  // No snapshot of what this user loaded. Treating MultiLookup as dirty here keeps the
  // value from page open and Get-latest never shows the other user's save.
  if (!hasBaselineMap) {
    if (element === 'MultiLookup') {
      return false;
    }
    return true;
  }
  const baseValue = Object.prototype.hasOwnProperty.call(baseline.fieldValues, fieldId)
    ? baseline.fieldValues[fieldId]
    : '';
  if (ID_VALUE_ELEMENTS.indexOf(element) !== -1) {
    return !sameIdSet(toIdList(currentValue), toIdList(baseValue));
  }
  return !fieldValuesAreEqual(currentValue, baseValue);
};

const findDraftFieldValue = (draftFieldGroups, fieldId) => {
  for (let s = 0; s < (draftFieldGroups || []).length; s++) {
    const section = draftFieldGroups[s];
    if (!section || section.type === 'subform') {
      continue;
    }
    const fields = section.data || [];
    for (let f = 0; f < fields.length; f++) {
      if (fields[f] && fields[f].id === fieldId) {
        return { found: true, value: fieldComparableValue(fields[f]), field: fields[f] };
      }
    }
  }
  return { found: false, value: undefined, field: null };
};

/** Ensure each row has subform_column_values aligned to section field defs. */
export const normalizeSubformRows = (rows = [], sectionData = []) => {
  return (rows || []).map((row) => {
    if (!row) {
      return row;
    }
    const existingCols = row.subform_column_values;
    if (Array.isArray(existingCols) && existingCols.length > 0) {
      return {
        ...row,
        subform_column_values: existingCols,
      };
    }
    return {
      ...row,
      id: row.id || '',
      display_postition: row.display_postition != null ? row.display_postition : 0,
      is_selected: !!row.is_selected,
      subform_column_values: (sectionData || []).map((item) => {
        const labelText =
          item && item.label && item.label.blocks && item.label.blocks[0]
            ? item.label.blocks[0].text
            : (item && (item.display_name || item.name)) || '';
        return {
          table_descriptor_id: item.id,
          display_name: labelText,
          selected_value: '',
          overridden: false,
        };
      }),
    };
  });
};

/** Build Redux subformData entries from fieldGroups (edit GET / post-merge). */
export const buildSubformDataFromFieldGroups = (fieldGroups = [], preferredSubformData = []) => {
  const preferredBySection = {};
  (preferredSubformData || []).forEach((sf) => {
    if (sf && sf.field_group_section_id) {
      preferredBySection[sf.field_group_section_id] = sf;
    }
  });
  const result = [];
  (fieldGroups || []).forEach((section) => {
    if (!section || section.type !== 'subform') {
      return;
    }
    const preferred = preferredBySection[section.id];
    // Prefer rows on the section (latest or merge-overlaid). Empty array is intentional.
    const sourceRows = Array.isArray(section.subform_rows)
      ? section.subform_rows
      : (preferred && preferred.subform_rows) || [];
    result.push({
      field_group_id: (preferred && preferred.field_group_id) || '',
      field_group_section_id: section.id,
      field_group_name: section.title || (preferred && preferred.field_group_name) || '',
      subform_rows: normalizeSubformRows(deepClone(sourceRows), section.data || []),
    });
  });
  return result;
};

const LOOKUP_DIRTY_FIELD_KEYS = [
  'displayValue',
  'dpnm',
  'stateValue',
  'ruleMapping',
  'required_query',
  'related_window_fields',
  'selectedValueText',
  'overridden',
];

/** Overlay draft value + Lookup/MultiLookup display helpers onto a latest field. */
export const applyDirtyDraftOntoField = (latestField, draftField) => {
  if (!latestField) {
    return latestField;
  }
  const nextField = {
    ...latestField,
    selectedValue: draftField.selectedValue,
  };
  LOOKUP_DIRTY_FIELD_KEYS.forEach((key) => {
    if (draftField[key] !== undefined) {
      nextField[key] = draftField[key];
    }
  });
  // Currency amount and code travel together. Copy the code the user still has on the draft.
  CURRENCY_COMPANION_KEYS.forEach((key) => {
    if (draftField[key] !== undefined) {
      nextField[key] = draftField[key];
    }
  });
  // MultiSelect checked options live on the field; keep draft options when present
  if (
    draftField.options &&
    (latestField.element === 'MultiSelect' || latestField.element === 'PickList')
  ) {
    nextField.options = draftField.options;
  }
  // File/Image: reducer stores uploads on .files (not selectedValue)
  if (isFileOrImageField(latestField) || isFileOrImageField(draftField)) {
    let draftFiles = [];
    if (Array.isArray(draftField.files)) {
      draftFiles = draftField.files;
    } else if (Array.isArray(draftField.selectedValue)) {
      draftFiles = draftField.selectedValue;
    }
    nextField.files = deepClone(draftFiles);
    if (draftField.overridden !== undefined) {
      nextField.overridden = draftField.overridden;
    } else {
      nextField.overridden = true;
    }
  }
  return nextField;
};

const isJsonValue = (value) => typeof value === 'string' && /^\s*[[{]/.test(value);

const parseMultiLookupEntries = (value) => {
  const byId = {};
  if (!isJsonValue(value)) {
    return byId;
  }
  try {
    let parsed = JSON.parse(value);
    parsed = Array.isArray(parsed) ? parsed : [parsed];
    parsed.forEach((entry) => {
      const id = entry && typeof entry === 'object' ? multiLookupRecordId(entry) : '';
      if (id && !byId[id]) {
        byId[id] = entry;
      }
    });
  } catch (e) {
    // malformed value: fall back to ids only
  }
  return byId;
};

const entryDisplayName = (entry) => {
  if (!entry || typeof entry !== 'object') {
    return '';
  }
  return String(entry.dpnm || entry.name || entry.label || entry.text || '').trim();
};

const tagRecordId = (tag) => {
  if (!tag) {
    return '';
  }
  if (typeof tag === 'string') {
    return tag.trim();
  }
  return String(tag.value || tag.metaTableId || tag.meta_table_id || tag.rowId || tag.id || '').trim();
};

const tagDisplayName = (tag) => {
  if (!tag || typeof tag !== 'object') {
    return '';
  }
  return String(
    tag.label || tag.text || tag.dpnm || tag.name || entryDisplayName(tag.data) || ''
  ).trim();
};

/**
 * When MultiLookup is dirty, keep the user's draft selection only.
 * Do NOT three-way-merge with latest (that re-introduces ids the user already has/dropped).
 * Deduplicate by record id across selectedValue / displayValue / stateValue.
 */
const retainMultiLookupDraftField = (latestField, draftField) => {
  const resultIds = uniqueIdList(toIdList(draftField.selectedValue));
  const namesById = {};
  const tagsById = {};

  (Array.isArray(draftField.stateValue) ? draftField.stateValue : []).forEach((tag) => {
    const id = tagRecordId(tag);
    if (!id || tagsById[id]) {
      return;
    }
    tagsById[id] = tag;
    const label = tagDisplayName(tag);
    if (label) {
      namesById[id] = label;
    }
  });

  const draftEntries = parseMultiLookupEntries(draftField.selectedValue);
  Object.keys(draftEntries).forEach((id) => {
    const name = entryDisplayName(draftEntries[id]);
    if (name && !namesById[id]) {
      namesById[id] = name;
    }
  });

  const displayNames = String(draftField.displayValue || '').split(',').map((n) => n.trim());
  toIdList(draftField.selectedValue).forEach((id, i) => {
    if (displayNames[i] && !namesById[id]) {
      namesById[id] = displayNames[i];
    }
  });

  const draftIsJson = isJsonValue(draftField.selectedValue);
  let nextSelectedValue;
  if (draftIsJson) {
    nextSelectedValue = JSON.stringify(
      resultIds.map((id) => {
        if (draftEntries[id]) {
          return draftEntries[id];
        }
        return {
          id,
          meta_table_id: id,
          dpnm: namesById[id] || id,
        };
      })
    );
  } else {
    nextSelectedValue = resultIds.join(',');
  }

  const nextStateValue = resultIds.map((id) => {
    if (tagsById[id]) {
      return tagsById[id];
    }
    const label = namesById[id] || id;
    return {
      label,
      text: label,
      id,
      value: id,
    };
  });
  const nextDisplayValue = resultIds.map((id) => namesById[id] || id).join(',');

  const nextField = {
    ...latestField,
    selectedValue: nextSelectedValue,
    displayValue: nextDisplayValue,
    stateValue: nextStateValue,
    overridden: draftField.overridden !== undefined ? draftField.overridden : true,
  };
  ['ruleMapping', 'related_window_fields', 'selectedValueText', 'dpnm'].forEach((key) => {
    if (draftField[key] !== undefined) {
      nextField[key] = draftField[key];
    }
  });
  // Specific selected ids are never "select all" mode — required_query=true makes one delete clear every tag.
  nextField.required_query = resultIds.length > 0
    ? false
    : (draftField.required_query !== undefined ? draftField.required_query : !!latestField.required_query);
  return nextField;
};

/**
 * Subform: start from latest DB rows, drop rows this user deleted, overlay only the cells
 * this user changed on rows they edited, and append rows this user added (no id yet).
 * Rows added/edited/deleted by the other process stay as saved in the DB.
 */
const mergeSubformRows = (latestRows = [], draftRows = [], baseRows) => {
  if (!Array.isArray(baseRows)) {
    return latestRows || [];
  }
  const baseById = {};
  baseRows.forEach((row) => {
    if (row && row.id) {
      baseById[row.id] = row;
    }
  });
  const draftById = {};
  (draftRows || []).forEach((row) => {
    if (row && row.id) {
      draftById[row.id] = row;
    }
  });

  const result = [];
  (latestRows || []).forEach((latestRow) => {
    const id = latestRow && latestRow.id;
    const baseRow = id ? baseById[id] : null;
    const draftRow = id ? draftById[id] : null;
    if (baseRow && !draftRow) {
      return;
    }
    if (!baseRow || !draftRow) {
      result.push(latestRow);
      return;
    }
    const baseValues = {};
    (baseRow.columns || []).forEach((col) => {
      baseValues[col.id] = col.v;
    });
    const draftCols = {};
    (draftRow.subform_column_values || []).forEach((col) => {
      if (col && col.table_descriptor_id) {
        draftCols[col.table_descriptor_id] = col;
      }
    });
    result.push({
      ...latestRow,
      subform_column_values: (latestRow.subform_column_values || []).map((col) => {
        const draftCol = col ? draftCols[col.table_descriptor_id] : null;
        if (
          draftCol &&
          !columnValuesAreEqual(columnComparableValue(draftCol), baseValues[col.table_descriptor_id])
        ) {
          return { ...col, ...draftCol };
        }
        return col;
      }),
    });
  });
  (draftRows || []).forEach((row) => {
    if (row && !row.id) {
      result.push(row);
    }
  });
  return result;
};

/**
 * Merge: start from latest DB fieldGroups, overlay only what this user changed (vs baseline).
 * Also returns merged subformData for setSubFormDataAction.
 */
export const mergeDirtyEditFormIntoLatest = ({
  latestFieldGroups = [],
  draftFieldGroups = [],
  draftSubformData = [],
  baseline = { fieldValues: {}, subformValues: {} },
}) => {
  const draftSubformBySection = {};
  (draftSubformData || []).forEach((sf) => {
    if (sf && sf.field_group_section_id) {
      draftSubformBySection[sf.field_group_section_id] = sf;
    }
  });
  const baselineSubformValues = (baseline && baseline.subformValues) || {};

  const mergedFieldGroups = deepClone(latestFieldGroups || []).map((section) => {
    if (!section) {
      return section;
    }
    if (section.type === 'subform') {
      const draftSf = draftSubformBySection[section.id];
      const draftRowsFromGroup = (draftFieldGroups || []).find(
        (s) => s && s.id === section.id && s.type === 'subform'
      );
      const draftRows =
        (draftSf && draftSf.subform_rows) ||
        (draftRowsFromGroup && draftRowsFromGroup.subform_rows) ||
        [];
      const mergedRows = mergeSubformRows(
        section.subform_rows || [],
        draftRows,
        baselineSubformValues[section.id]
      );
      return {
        ...section,
        subform_rows: normalizeSubformRows(deepClone(mergedRows), section.data || []),
      };
    }

    return {
      ...section,
      data: (section.data || []).map((field) => {
        if (!field || !field.id) {
          return field;
        }
        const draftHit = findDraftFieldValue(draftFieldGroups, field.id);
        const fieldElement = resolveFieldElement(field);
        if (
          draftHit.found &&
          draftHit.field &&
          isFieldDirty(field.id, draftHit.value, baseline, fieldElement)
        ) {
          if (fieldElement === 'MultiLookup') {
            return retainMultiLookupDraftField(field, draftHit.field);
          }
          // Lookup needs displayValue (label)
          return applyDirtyDraftOntoField(field, draftHit.field);
        }
        return field;
      }),
    };
  });

  const mergedSubformData = buildSubformDataFromFieldGroups(
    mergedFieldGroups,
    draftSubformData
  );

  return {
    fieldGroups: mergedFieldGroups,
    subformData: mergedSubformData,
  };
};

export const isConcurrentEditConflict = (response, httpStatus) => {
  if (httpStatus === 409) {
    return true;
  }
  if (response && Number(response.code) === 409) {
    return true;
  }
  // Backend record-lock violation (HTTP 409 body often includes this)
  const violations = (response && response.violations) || [];
  if (violations.some((v) => {
    const field = String((v && (v.field || v.field_id || v.fieldId || v.field_name || v.fieldName)) || '').toLowerCase();
    const msg = String((v && v.message) || '').toLowerCase();
    return field === 'record_lock' || msg.indexOf('currently being updated by another') !== -1;
  })) {
    return true;
  }
  const message = String((response && response.message) || '').toLowerCase();
  if (!message) {
    return false;
  }
  const conflictHints = [
    'currently being updated by another',
    'updated by another process',
    'another user has updated',
    'record was modified by another',
    'please get the latest',
    'get the latest data',
  ];
  if (conflictHints.some((hint) => message.indexOf(hint) !== -1)) {
    return true;
  }
  return false;
};

export const CONCURRENT_EDIT_DEFAULT_MESSAGE =
  'This record is currently being updated by another process. Get the latest data to keep your changes and continue.';

/**
 * Normalize axios / http interceptor errors.
 * shipcrm services/http rejects with `error.response` (the response object), not the axios error.
 */
export const normalizeHttpError = (error) => {
  if (!error) {
    return { status: null, data: null };
  }
  if (error.response) {
    return {
      status: error.response.status,
      data: error.response.data,
    };
  }
  // Rejected as response object: { status, data, ... }
  if (error.status != null && error.data !== undefined) {
    return {
      status: error.status,
      data: error.data,
    };
  }
  // Body-shaped payload
  if (error.code != null || error.message != null) {
    return {
      status: Number(error.code) || null,
      data: error,
    };
  }
  return { status: null, data: null };
};

export { deepClone };
